import React, { useEffect, useRef } from 'react';
import { HEALTH_CHECK_COPY, HEALTH_CHECK_QUESTIONS } from '../content/healthCheck';
import Button from '../primitives/Button';
import type { HcAction, HcState } from './healthCheckMachine';

interface HealthCheckQuestionsProps {
  state: HcState;
  dispatch: React.Dispatch<HcAction>;
}

/**
 * One question at a time as a real radio group. Focus moves to the question
 * when it changes so a screen reader announces it; the counter is live.
 */
export default function HealthCheckQuestions({ state, dispatch }: HealthCheckQuestionsProps) {
  const question = HEALTH_CHECK_QUESTIONS[state.index];
  const legendRef = useRef<HTMLLegendElement | null>(null);
  const chosen = question ? state.answers[question.id] : undefined;
  const total = HEALTH_CHECK_QUESTIONS.length;

  useEffect(() => {
    legendRef.current?.focus({ preventScroll: true });
  }, [state.index]);

  if (!question) return null;

  return (
    <>
      <p className="mk-hc-panel__count t-details" aria-live="polite">
        {state.index + 1} / {total}
      </p>
      <fieldset className="mk-hc-fieldset">
        <legend ref={legendRef} tabIndex={-1}>
          {question.prompt}
        </legend>
        {question.options.map((option) => (
          <label key={option.id} className="mk-hc-option">
            <input
              type="radio"
              name={question.id}
              value={option.id}
              checked={chosen === option.id}
              onChange={() => dispatch({ type: 'ANSWER', questionId: question.id, optionId: option.id })}
            />
            <span className="t-body">{option.label}</span>
          </label>
        ))}
      </fieldset>
      <div className="mk-hc-actions">
        <Button variant="link" onClick={() => dispatch({ type: 'BACK' })}>
          {HEALTH_CHECK_COPY.back}
        </Button>
        <Button variant="fill" disabled={!chosen} onClick={() => dispatch({ type: 'NEXT' })}>
          {HEALTH_CHECK_COPY.next}
        </Button>
      </div>
    </>
  );
}
